import dayjs from "dayjs";
import { navigate } from "raviger";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import { Avatar } from "@/components/Common/Avatar";

import { useAuthContext } from "@/hooks/useAuthUser";

import { formatName } from "@/Utils/utils";
import { storeUserInLocalStorage } from "@/types/scheduling/schedule";
import { UserReadMinimal } from "@/types/user/user";

interface Props {
  user: UserReadMinimal;
  className?: string;
  facilityId: string;
}

export function UserCard({ user, className, facilityId }: Props) {
  const { t } = useTranslation();
  const { patientToken: tokenData } = useAuthContext();

  const returnLink = useMemo(() => {
    if (
      tokenData &&
      Object.keys(tokenData).length > 0 &&
      dayjs(tokenData.createdAt).isAfter(dayjs().subtract(14, "minutes"))
    ) {
      return `/facility/${facilityId}/appointments/${user.id}/book-appointment`;
    }
    return `/facility/${facilityId}/appointments/${user.id}/otp/send`;
  }, [tokenData, facilityId, user.id]);

  return (
    <Card className={cn("overflow-hidden bg-white", className)}>
      <div className="flex flex-col gap-4 p-5">
        <div className="flex flex-row gap-4">
          <Avatar
            imageUrl={user.profile_picture_url}
            name={formatName(user, true)}
            className="size-12 rounded-lg"
          />

          <div className="flex grow flex-col min-w-0">
            <h3 className="truncate text-lg font-bold">{formatName(user)}</h3>
            <p className="text-sm text-gray-500">{user.user_type}</p>
          </div>
        </div>
        <Button
          variant="white"
          className="text-primary-600"
          onClick={() => {
            storeUserInLocalStorage(user);
            navigate(returnLink);
          }}
        >
          {t("book_appointment")}
        </Button>
      </div>
    </Card>
  );
}
